import { Button, SecondaryButton } from "@jammable/ui-core";
import { SubscriptionUpgradeType } from "@jammable/clientapi/src/payments";
import { Spinner } from "@jammable/ui-components/Loader";
import { useUpgradeOptions } from "./hooks/useUpgradeOptions";
import { formatCurrency } from "./utils";
import { FaFire } from "react-icons/fa";

interface UpgradeProps {
  type: SubscriptionUpgradeType;
  fallback?: () => void;
  callback?: () => void;
}

export function Upgrade({
  type = "Jammable Creator",
  fallback,
  callback,
}: UpgradeProps) {
  const {
    isLoadingPrices,
    isUpgrading,
    upgradeOfType,
    currency,
    handleUpgrade,
  } = useUpgradeOptions({ type, fallback, callback });

  if (isLoadingPrices || !upgradeOfType)
    return (
      <div className="tw-m-20 tw-flex tw-justify-center tw-items-center tw-gap-4 tw-flex-col">
        <Spinner className="tw-border-4" size={5} />
      </div>
    );

  const { title, conversions, models, amount, description, plan } =
    upgradeOfType;

  // Colors for the feature tags - alternating between a few options
  const featureColors = [
    "tw-bg-lime-300",
    "tw-bg-purple-300",
    "tw-bg-blue-300",
    "tw-bg-pink-300",
    "tw-bg-yellow-300",
  ];

  return (
    <section className="tw-flex tw-flex-col tw-gap-0 tw-max-w-md">
      <div className="tw-flex tw-items-center">
        <h2 className="tw-bg-black tw-w-max tw-p-2 tw-rounded-xl tw-px-3 tw-text-white tw-text-2xl tw-font-black tw-my-0">
          {title.toUpperCase()}
        </h2>
        <div className="tw-text-sm tw-bg-lime-300 tw-text-black tw-font-black tw-py-1.5 tw-px-2 tw-rounded-r-xl tw-flex tw-items-center">
          MOST POPULAR{" "}
          <span className="tw-ml-1">
            <FaFire />
          </span>
        </div>
      </div>

      <div className="tw-text-sm tw-mt-2 tw-font-bold tw-uppercase tw-tracking-widest tw-text-black/40">
        Join thousands of creators on the best value subscription
      </div>

      {isLoadingPrices ? (
        <div className="tw-my-6">
          <div className="tw-mx-auto tw-h-40 tw-min-w-96 tw-animate-pulse tw-rounded-2xl tw-bg-gray-300"></div>
          <span className="tw-sr-only">Loading...</span>
        </div>
      ) : (
        <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-2 tw-rounded-3xl tw-border-3 tw-bg-purple-600 tw-px-6 tw-py-5 tw-font-bold tw-text-white">
          <div className="tw-text-xl tw-font-bold">
            {conversions} Covers + {models} Voices
          </div>
          <div className="tw-flex tw-items-baseline">
            <span className="tw-text-4xl tw-font-black">
              {currency && formatCurrency(amount!, currency!)}
            </span>
            <span className="tw-ml-1 tw-text-sm tw-font-semibold">
              / {plan.interval}
            </span>
          </div>

          {description && (
            <div className="tw-flex tw-gap-3 tw-flex-wrap tw-mt-4">
              {description.map((desc, index) => (
                <div
                  key={desc.title}
                  className={`${desc.color} tw-p-2 tw-rounded-lg tw-text-sm tw-font-black tw-uppercase tw-text-black tw-tracking-wide tw-flex tw-items-center tw-transition-all tw-duration-200 hover:tw-opacity-90`}
                >
                  {desc.title}
                </div>
              ))}
            </div>
          )}
          <SecondaryButton
            className="tw-mt-4 tw-font-black tw-border-none tw-w-full tw-text-xl tw-h-14 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-200 tw-text-black"
            onClick={handleUpgrade}
            disabled={isUpgrading}
          >
            {isLoadingPrices || isUpgrading ? (
              <Spinner size={4} className="tw-border-black tw-border-3" />
            ) : (
              `UPGRADE`
            )}
          </SecondaryButton>
        </div>
      )}
    </section>
  );
}
